const FORM_SELECTOR = '[data-birthchart-form]';

const CREATION_SURFACES = new Set([
    'homepage',
    'horoscope',
    'maps',
    'unknown',
]);

const ENTRY_POINTS = new Set([
    'direct',
    'navigation',
    'maps_dashboard',
    'compatibility',
    'daily_horoscope',
    'email_confirmation',
    'not_found',
    'subscription',
    'unknown',
]);

const USER_TYPES = new Set([
    'authenticated',
    'anonymous',
]);

const EXACT_EVENT_NAMES = new Set([
    'birthchart_form_start',
    'birthchart_form_submit',
    'location_search_start',
    'location_search',
    'location_select',
]);

const INTERACTION_EVENT_NAMES = [
    'focusin',
    'input',
    'change',
];

const REQUIRED_ELEMENT_SELECTORS = {
    dateInput: '#date',
    locationInput: '#location',
    nameInput: '#name',
    placeInput: '#searchPlaceID',
    latitudeInput: '#searchLatitude',
    longitudeInput: '#searchLongitude',
    timeInput: '#hour',
    unknownTimeCheckbox: '#unknown-time',
};

const OPTIONAL_ELEMENT_SELECTORS = {
    errorSummary: '[data-birthchart-error-summary]',
    nameError: '[data-birthchart-field-error="name"]',
    dateError: '[data-birthchart-field-error="date"]',
    timeError: '[data-birthchart-field-error="hour"]',
    locationError: '[data-birthchart-field-error="location"]',
    submitButton: '[data-birthchart-submit]',
    submitLabel: '[data-birthchart-submit-label]',
    submitStatus: '[data-birthchart-submit-status]',
    unknownTimeConsequence: '[data-unknown-time-consequence]',
    reviewPanel: '[data-birthchart-review]',
    reviewName: '[data-birthchart-review-value="name"]',
    reviewDate: '[data-birthchart-review-value="date"]',
    reviewTime: '[data-birthchart-review-value="hour"]',
    reviewLocation: '[data-birthchart-review-value="location"]',
    atlasFields: '[data-birthchart-atlas-fields]',
    reviewBack: '[data-birthchart-review-back]',
    dataBack: '[data-birthchart-data-back]',
    editName: '[data-birthchart-edit="name"]',
    editDate: '[data-birthchart-edit="date"]',
    editTime: '[data-birthchart-edit="hour"]',
    editLocation: '[data-birthchart-edit="location"]',
};

const PROGRESS_ELEMENT_SELECTORS = {
    dataStep: '[data-birthchart-progress-step="data"]',
    dataStepStatus: '[data-birthchart-progress-step="data"] [data-birthchart-progress-status]',
    reviewStep: '[data-birthchart-progress-step="review"]',
    reviewStepStatus: '[data-birthchart-progress-step="review"] [data-birthchart-progress-status]',
    calculationStep: '[data-birthchart-progress-step="calculation"]',
    calculationStepStatus: '[data-birthchart-progress-step="calculation"] [data-birthchart-progress-status]',
};

function exactContextFromForm(form) {
    try {
        const context = {
            creation_surface: form.dataset.creationSurface,
            entry_point: form.dataset.entryPoint,
            user_type: form.dataset.userType,
        };

        if (
            !CREATION_SURFACES.has(context.creation_surface)
            || !ENTRY_POINTS.has(context.entry_point)
            || !USER_TYPES.has(context.user_type)
        ) {
            return null;
        }

        return context;
    } catch (error) {
        return null;
    }
}

function trackExactEvent(eventName, context, gtagFunction) {
    if (
        !EXACT_EVENT_NAMES.has(eventName)
        || context === null
        || typeof gtagFunction !== 'function'
    ) {
        return false;
    }

    const payload = {
        creation_surface: context.creation_surface,
        entry_point: context.entry_point,
        user_type: context.user_type,
    };

    if (
        !CREATION_SURFACES.has(payload.creation_surface)
        || !ENTRY_POINTS.has(payload.entry_point)
        || !USER_TYPES.has(payload.user_type)
    ) {
        return false;
    }

    try {
        gtagFunction('event', eventName, payload);

        return true;
    } catch (error) {
        return false;
    }
}

function isValidDateValue(value, today = new Date()) {
    if (typeof value !== 'string') {
        return false;
    }

    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
    if (match === null) {
        return false;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);

    return year >= 1801
        && date <= new Date(today.getFullYear(), today.getMonth(), today.getDate())
        && date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day;
}

function isValidTimeValue(value) {
    if (typeof value !== 'string') {
        return false;
    }

    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (match === null) {
        return false;
    }

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
}

function queryBirthchartForm(root) {
    try {
        return root?.querySelector(FORM_SELECTOR) ?? null;
    } catch (error) {
        return null;
    }
}

function queryRequiredElements(form) {
    const elements = {};

    try {
        for (const [name, selector] of Object.entries(REQUIRED_ELEMENT_SELECTORS)) {
            const element = form.querySelector(selector);
            if (element === null) {
                return null;
            }

            elements[name] = element;
        }
    } catch (error) {
        return null;
    }

    return elements;
}

function queryOptionalElements(root, selectors = OPTIONAL_ELEMENT_SELECTORS) {
    const elements = {};

    for (const [name, selector] of Object.entries(selectors)) {
        try {
            elements[name] = root.querySelector(selector);
        } catch (error) {
            elements[name] = null;
        }
    }

    return elements;
}

function isBubblingFormInteraction(form, event) {
    try {
        return event.target !== null
            && event.target !== form
            && form.contains(event.target);
    } catch (error) {
        return false;
    }
}

function setCustomValidity(input, message) {
    try {
        input.setCustomValidity(message);

        return true;
    } catch (error) {
        return false;
    }
}

function setAttribute(element, name, value) {
    try {
        element?.setAttribute(name, value);

        return element !== null && typeof element !== 'undefined';
    } catch (error) {
        return false;
    }
}

function removeAttribute(element, name) {
    try {
        element?.removeAttribute(name);

        return element !== null && typeof element !== 'undefined';
    } catch (error) {
        return false;
    }
}

function setText(element, value) {
    try {
        if (element === null || typeof element === 'undefined') {
            return false;
        }

        element.textContent = value;

        return true;
    } catch (error) {
        return false;
    }
}

function focusElement(element) {
    try {
        if (typeof element?.focus !== 'function') {
            return false;
        }

        element.focus();

        return true;
    } catch (error) {
        return false;
    }
}

function checkNativeValidity(form) {
    try {
        return typeof form.checkValidity === 'function' && form.checkValidity();
    } catch (error) {
        return false;
    }
}

function reportNativeValidity(form) {
    try {
        if (typeof form.reportValidity === 'function') {
            form.reportValidity();
        }
    } catch (error) {
        // Browser validity UI is optional and must never break the form handler.
    }
}

function preventSubmission(event) {
    try {
        event.preventDefault();
    } catch (error) {
        // A malformed synthetic event is already ineligible for normal submission.
    }
}

function stringValue(value) {
    return value === null || typeof value === 'undefined' ? '' : String(value);
}

function isValidCoordinate(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }

    return typeof value === 'string'
        && value.trim() !== ''
        && Number.isFinite(Number(value));
}

function validLocationSuggestion(suggestion) {
    try {
        const value = suggestion.value;
        const labelIsValid = typeof suggestion.label === 'string' && suggestion.label.trim() !== '';
        const placeIsValid = typeof value.placeID === 'string' && value.placeID.trim() !== '';
        const coordinatesAreValid = isValidCoordinate(value.latitude)
            && isValidCoordinate(value.longitude);

        return labelIsValid && (placeIsValid || coordinatesAreValid);
    } catch (error) {
        return false;
    }
}

function initializeBirthchartForm(root, { gtagFunction = null } = {}) {
    const form = queryBirthchartForm(root);
    if (form === null) {
        return null;
    }

    const elements = queryRequiredElements(form);
    if (elements === null) {
        return null;
    }

    const optionalElements = queryOptionalElements(form);
    const progressElements = optionalElements.reviewPanel === null
        ? {}
        : queryOptionalElements(root, PROGRESS_ELEMENT_SELECTORS);

    if (optionalElements.errorSummary !== null) {
        setAttribute(form, 'novalidate', '');
    }

    const context = exactContextFromForm(form);
    let locationSelected = validLocationSuggestion({
        label: elements.locationInput.value,
        value: {
            latitude: elements.latitudeInput.value,
            longitude: elements.longitudeInput.value,
            placeID: elements.placeInput.value,
        },
    });
    let startTracked = false;
    let submitTracked = false;
    let submitting = false;
    let reviewPresented = false;
    const calculationLabel = stringValue(optionalElements.submitLabel?.textContent).trim()
        || 'Calcular meu mapa natal';
    const reviewLabel = optionalElements.atlasFields === null ? 'Revisar os dados' : 'Revisar dados';

    function hideErrorSummary() {
        setAttribute(optionalElements.errorSummary, 'hidden', '');
    }

    function showErrorSummary() {
        removeAttribute(optionalElements.errorSummary, 'hidden');
        focusElement(optionalElements.errorSummary);
    }

    function clearFieldError(input, errorElement) {
        setCustomValidity(input, '');
        removeAttribute(input, 'aria-invalid');
        setAttribute(errorElement, 'hidden', '');
    }

    function showFieldError(input, errorElement, message) {
        setCustomValidity(input, message);
        setAttribute(input, 'aria-invalid', 'true');
        setText(errorElement, message);
        removeAttribute(errorElement, 'hidden');
    }

    function setSubmissionBusy(isBusy) {
        submitting = isBusy;
        setAttribute(form, 'aria-busy', isBusy ? 'true' : 'false');
        setAttribute(optionalElements.submitButton, 'aria-busy', isBusy ? 'true' : 'false');

        if (isBusy) {
            setAttribute(optionalElements.submitButton, 'disabled', '');
            removeAttribute(optionalElements.submitStatus, 'hidden');

            return;
        }

        removeAttribute(optionalElements.submitButton, 'disabled');
        setAttribute(optionalElements.submitStatus, 'hidden', '');
    }

    function setProgressStepState(step, status, state, statusText) {
        if (state === '') {
            removeAttribute(step, 'data-state');
        } else {
            setAttribute(step, 'data-state', state);
        }

        if (state === 'current') {
            setAttribute(step, 'aria-current', 'step');
        } else {
            removeAttribute(step, 'aria-current');
        }

        setText(status, statusText);
    }

    function showDataProgress() {
        setProgressStepState(
            progressElements.dataStep,
            progressElements.dataStepStatus,
            'current',
            'Etapa atual',
        );
        setProgressStepState(
            progressElements.reviewStep,
            progressElements.reviewStepStatus,
            '',
            'Próxima etapa',
        );
        setProgressStepState(
            progressElements.calculationStep,
            progressElements.calculationStepStatus,
            '',
            'Etapa futura',
        );
    }

    function showReviewProgress() {
        setProgressStepState(
            progressElements.dataStep,
            progressElements.dataStepStatus,
            'complete',
            'Concluída',
        );
        setProgressStepState(
            progressElements.reviewStep,
            progressElements.reviewStepStatus,
            'current',
            'Etapa atual',
        );
        setProgressStepState(
            progressElements.calculationStep,
            progressElements.calculationStepStatus,
            '',
            'Próxima etapa',
        );
    }

    function showCalculationProgress() {
        setProgressStepState(
            progressElements.dataStep,
            progressElements.dataStepStatus,
            'complete',
            'Concluída',
        );
        setProgressStepState(
            progressElements.reviewStep,
            progressElements.reviewStepStatus,
            'complete',
            'Concluída',
        );
        setProgressStepState(
            progressElements.calculationStep,
            progressElements.calculationStepStatus,
            'current',
            'Etapa atual',
        );
    }

    function presentReview() {
        setText(optionalElements.reviewName, elements.nameInput.value.trim());
        setText(optionalElements.reviewDate, elements.dateInput.value);
        setText(
            optionalElements.reviewTime,
            elements.unknownTimeCheckbox.checked
                ? 'Não informado — mapa sem Ascendente e casas'
                : elements.timeInput.value,
        );
        setText(optionalElements.reviewLocation, elements.locationInput.value);
        setAttribute(optionalElements.atlasFields, 'hidden', '');
        setAttribute(optionalElements.dataBack, 'hidden', '');
        removeAttribute(optionalElements.reviewBack, 'hidden');
        removeAttribute(optionalElements.reviewPanel, 'hidden');
        setText(optionalElements.submitLabel, calculationLabel);
        reviewPresented = true;
        showReviewProgress();
        focusElement(optionalElements.reviewPanel);
    }

    function resetReview() {
        if (!reviewPresented) {
            return;
        }

        setAttribute(optionalElements.reviewPanel, 'hidden', '');
        removeAttribute(optionalElements.atlasFields, 'hidden');
        removeAttribute(optionalElements.dataBack, 'hidden');
        setAttribute(optionalElements.reviewBack, 'hidden', '');
        setText(optionalElements.submitLabel, reviewLabel);
        reviewPresented = false;
        showDataProgress();
    }

    function editReviewedField(input) {
        resetReview();
        focusElement(input);
    }

    function resetSubmissionState() {
        setSubmissionBusy(false);

        if (optionalElements.reviewPanel === null) {
            return;
        }

        if (reviewPresented) {
            showReviewProgress();
        } else {
            showDataProgress();
        }
    }

    function track(eventName) {
        return trackExactEvent(eventName, context, gtagFunction);
    }

    function trackLocationEvent(eventName) {
        trackStart();

        return track(eventName);
    }

    function trackStart() {
        if (startTracked) {
            return false;
        }

        startTracked = track('birthchart_form_start');

        return startTracked;
    }

    function trackInteraction(event) {
        if (isBubblingFormInteraction(form, event)) {
            trackStart();
        }
    }

    function clearLocationSelection() {
        locationSelected = false;
        elements.placeInput.value = '';
        elements.latitudeInput.value = '';
        elements.longitudeInput.value = '';
        clearFieldError(elements.locationInput, optionalElements.locationError);
    }

    function selectLocation(suggestion) {
        if (!validLocationSuggestion(suggestion)) {
            clearLocationSelection();

            return false;
        }

        elements.placeInput.value = stringValue(suggestion.value.placeID);
        elements.latitudeInput.value = stringValue(suggestion.value.latitude);
        elements.longitudeInput.value = stringValue(suggestion.value.longitude);
        elements.locationInput.value = suggestion.label;
        clearFieldError(elements.locationInput, optionalElements.locationError);
        locationSelected = true;

        return true;
    }

    function updateUnknownTimeState() {
        if (elements.unknownTimeCheckbox.checked) {
            elements.timeInput.removeAttribute('required');
            elements.timeInput.setAttribute('disabled', '');
            elements.timeInput.value = '';
            clearFieldError(elements.timeInput, optionalElements.timeError);
            setText(
                optionalElements.unknownTimeConsequence,
                'Horário não informado: o mapa será calculado sem Ascendente e casas.',
            );

            return;
        }

        elements.timeInput.setAttribute('required', '');
        elements.timeInput.removeAttribute('disabled');
        setText(
            optionalElements.unknownTimeConsequence,
            'Se você marcar esta opção, o mapa será calculado sem Ascendente e casas.',
        );
    }

    function validateName() {
        const valid = typeof elements.nameInput.value === 'string'
            && elements.nameInput.value.trim() !== '';

        if (valid) {
            clearFieldError(elements.nameInput, optionalElements.nameError);
        } else {
            showFieldError(
                elements.nameInput,
                optionalElements.nameError,
                'Informe um nome de referência.',
            );
        }

        return valid;
    }

    function validateDate() {
        const valid = isValidDateValue(elements.dateInput.value);

        if (valid) {
            clearFieldError(elements.dateInput, optionalElements.dateError);
        } else {
            showFieldError(
                elements.dateInput,
                optionalElements.dateError,
                'Informe uma data de nascimento entre 01/01/1801 e hoje, no formato DD/MM/AAAA.',
            );
        }

        return valid;
    }

    function validateTime() {
        if (elements.unknownTimeCheckbox.checked) {
            clearFieldError(elements.timeInput, optionalElements.timeError);

            return true;
        }

        const valid = isValidTimeValue(elements.timeInput.value);

        if (valid) {
            clearFieldError(elements.timeInput, optionalElements.timeError);
        } else {
            showFieldError(
                elements.timeInput,
                optionalElements.timeError,
                'Informe um horário entre 00:00 e 23:59.',
            );
        }

        return valid;
    }

    function validateLocation() {
        if (locationSelected) {
            clearFieldError(elements.locationInput, optionalElements.locationError);
        } else {
            showFieldError(
                elements.locationInput,
                optionalElements.locationError,
                'Escolha uma cidade entre os resultados da busca.',
            );
        }

        return locationSelected;
    }

    function handleSubmit(event) {
        if (submitting) {
            preventSubmission(event);

            return false;
        }

        const customResults = [
            validateName(),
            validateDate(),
            validateTime(),
            validateLocation(),
        ];
        const customValid = customResults.every(Boolean);
        const nativeValid = checkNativeValidity(form);

        if (!customValid || !nativeValid) {
            preventSubmission(event);
            if (optionalElements.errorSummary === null) {
                reportNativeValidity(form);
            } else {
                showErrorSummary();
            }

            return false;
        }

        if (event?.defaultPrevented === true) {
            return false;
        }

        if (optionalElements.reviewPanel !== null && !reviewPresented) {
            preventSubmission(event);
            hideErrorSummary();
            trackStart();
            presentReview();

            return false;
        }

        hideErrorSummary();
        trackStart();
        if (!submitTracked) {
            submitTracked = track('birthchart_form_submit');
        }

        showCalculationProgress();
        setSubmissionBusy(true);

        return true;
    }

    for (const eventName of INTERACTION_EVENT_NAMES) {
        form.addEventListener(eventName, trackInteraction);
    }

    elements.nameInput.addEventListener('input', () => {
        clearFieldError(elements.nameInput, optionalElements.nameError);
        resetReview();
    });
    elements.dateInput.addEventListener('input', () => {
        clearFieldError(elements.dateInput, optionalElements.dateError);
        resetReview();
    });
    elements.timeInput.addEventListener('input', () => {
        clearFieldError(elements.timeInput, optionalElements.timeError);
        resetReview();
    });
    elements.locationInput.addEventListener('input', () => {
        clearLocationSelection();
        resetReview();
    });
    elements.unknownTimeCheckbox.addEventListener('change', () => {
        updateUnknownTimeState();
        resetReview();
    });
    optionalElements.editName?.addEventListener('click', () => {
        editReviewedField(elements.nameInput);
    });
    optionalElements.editDate?.addEventListener('click', () => {
        editReviewedField(elements.dateInput);
    });
    optionalElements.editTime?.addEventListener('click', () => {
        editReviewedField(elements.unknownTimeCheckbox.checked
            ? elements.unknownTimeCheckbox
            : elements.timeInput);
    });
    optionalElements.editLocation?.addEventListener('click', () => {
        editReviewedField(elements.locationInput);
    });
    optionalElements.reviewBack?.addEventListener('click', () => {
        editReviewedField(elements.nameInput);
    });
    form.addEventListener('submit', handleSubmit);
    updateUnknownTimeState();

    if (optionalElements.reviewPanel !== null) {
        setText(optionalElements.submitLabel, reviewLabel);
        showDataProgress();
    }

    return {
        elements,
        form,
        isSubmitting: () => submitting,
        isLocationSelected: () => locationSelected,
        resetSubmissionState,
        selectLocation,
        trackLocationSearch: () => trackLocationEvent('location_search'),
        trackLocationSearchStart: () => trackLocationEvent('location_search_start'),
        trackLocationSelect: () => trackLocationEvent('location_select'),
    };
}

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        exactContextFromForm,
        initializeBirthchartForm,
        isValidDateValue,
        isValidTimeValue,
        trackExactEvent,
    };
}
